import { Component, Inject, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { OkrConvergenceService } from "src/app/shared/services/okr-convergence.service";
import { ScorecardService } from "src/app/shared/services/scorecard.service";
import textConfiguration from "src/assets/static-text-configuration.json";
import { getFormattedValWithSign } from "src/app/shared/components/scorecard-matrix/scorecard/utils/utils";
import { CommonService } from "src/app/shared/services/common.service";

@Component({
  selector: "app-metrics-what-if",
  templateUrl: "./metrics-what-if.component.html",
  styleUrls: ["./metrics-what-if.component.scss"],
})
export class MetricsWhatIfComponent implements OnInit {
  staticText: any = textConfiguration;
  displayLabels: any;
  title: string = "What-If Reasoning";
  metricIndexMap = new Map<string, number>();
  currentMetricId: any;
  averageValue: number = 0;
  metric_data: any;
  initial_metric_data: any;
  query_params: {
    portfolio_id: any;
    subportfolio_id: any;
    program_id: any;
    project_id: any;
  };
  breadCrumbData: any;
  selectedFilterLevel: string;
  minCap: number;
  maxCap: number;
  isDataLoaded: boolean = false;
  metric_summaries: any;
  constructor(
    public dialogRef: MatDialogRef<MetricsWhatIfComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly scorecardService: ScorecardService,
    private readonly okrConvergenceService: OkrConvergenceService,
    private readonly commonService: CommonService
  ) {}

  ngOnInit(): void {
    this.displayLabels = this.commonService.getLabels();
    this.currentMetricId = this.data?.metricId;
    if (this.data?.page === "okr convergence") {
      this.title = "OKR What-If";
    }
    this.query_params = {
      portfolio_id: this.data?.portfolioId,
      subportfolio_id: this.data?.subportfolioId,
      program_id: this.data?.programId,
      project_id: this.data?.projectId,
    };
    (
      Object.keys(this.query_params) as (keyof typeof this.query_params)[]
    ).forEach((key) => {
      if (this.query_params[key] == null) {
        delete this.query_params[key];
      }
    });
    // original data for reset
    if (this.data?.page === "balance scorecard") {
      this.scorecardService
        .getLatestMetricValues(this.currentMetricId, this.query_params)
        .subscribe((data: any) => {
          this.initial_metric_data = data;
          this.metric_data = JSON.parse(
            JSON.stringify(this.initial_metric_data)
          );
          this.calculateAverage();
          this.getBreadCrumbData(this.query_params, this.metric_data);
          this.selectedFilterLevel = this.getselectedFilterLevel();
          this.setMinMax();
          this.isDataLoaded = true;
        });
    } else {
      this.okrConvergenceService
        .getWhatIfMetricsValues(this.data?.qParams)
        .subscribe((resp: any) => {
          this.initial_metric_data = resp;
          this.metric_data = JSON.parse(
            JSON.stringify(this.initial_metric_data)
          );
          this.getBreadCrumbData(this.query_params, this.metric_data);
          this.selectedFilterLevel = this.getselectedFilterLevel();
          this.setMinMaxGoalMetrics();
          this.initializeMetricsBarLength();
          this.buildMetricSummaries();
          this.isDataLoaded = true;
        });
    }
  }

  buildMetricIndexMap() {
    this.metricIndexMap.clear();
    this.metric_summaries.forEach((summary: any, index: number) => {
      this.metricIndexMap.set(summary.display_name, index + 1);
    });
  }

  private buildMetricSummaries(): void {
    const summariesMap = new Map<number, any>();

    this.metric_data.portfolios.forEach((portfolio: any) => {
      portfolio.sub_portfolios.forEach((sub: any) => {
        sub.programs.forEach((program: any) => {
          program.projects.forEach((project: any) => {
            project.metrics.forEach((metric: any) => {
              if (!summariesMap.has(metric.metric_id)) {
                summariesMap.set(metric.metric_id, {
                  metric_id: metric.metric_id,
                  display_name: metric.display_name,
                  unit: metric.unit,
                  mos_type_name: metric.mos_type_name,
                  min_value: metric.min_value,
                  max_value: metric.max_value,
                  on_track_value: metric.on_track_value,
                  off_track_value: metric.off_track_value,
                  at_risk_value: metric.at_risk_value,
                  status: metric.status,
                  averageValue: 0,
                });
              }
            });
          });
        });
      });
    });
    this.metric_summaries = Array.from(summariesMap.values());
    this.buildMetricIndexMap();
    // compute initial averages
    this.calculateMetricSummaries();
  }

  private initializeMetricsBarLength(): void {
    this.metric_data.portfolios.forEach((portfolio: any) => {
      portfolio.sub_portfolios.forEach((sub: any) => {
        sub.programs.forEach((program: any) => {
          program.projects.forEach((project: any) => {
            project.metrics.forEach((metric: any) => {
              metric.barLength = this.calculateBarLengthGoalMetric(
                metric.value,
                metric
              );
            });
          });
        });
      });
    });
  }

  setMinMax() {
    const fallbackMin = 0;
    const fallbackMax = 100;

    let parsedMin =
      this.metric_data?.min_value != null
        ? parseFloat(this.metric_data.min_value)
        : NaN;
    this.minCap = !isNaN(parsedMin) ? parsedMin : fallbackMin;

    let parsedMax =
      this.metric_data?.max_value != null
        ? parseFloat(this.metric_data.max_value)
        : NaN;
    this.maxCap = !isNaN(parsedMax) ? parsedMax : fallbackMax;

    if (this.minCap > this.maxCap) {
      [this.minCap, this.maxCap] = [this.maxCap, this.minCap];
    }
  }

  setMinMaxGoalMetrics() {
    const fallbackMin = 0;
    const fallbackMax = 100;

    this.metric_data.portfolios.forEach((portfolio: any) => {
      portfolio.sub_portfolios.forEach((sub: any) => {
        sub.programs.forEach((program: any) => {
          program.projects.forEach((project: any) => {
            project.metrics?.forEach((metric: any) => {
              let parsedMin =
                metric.min_value != null
                  ? parseFloat(metric.min_value)
                  : fallbackMin;

              metric.minCap = !isNaN(parsedMin) ? parsedMin : fallbackMin;

              let parsedMax =
                metric.max_value != null
                  ? parseFloat(metric.max_value)
                  : fallbackMax;

              metric.maxCap = !isNaN(parsedMax) ? parsedMax : fallbackMax;

              if (metric.minCap > metric.maxCap) {
                [metric.minCap, metric.maxCap] = [metric.maxCap, metric.minCap]; // Swap them
              }
            });
          });
        });
      });
    });
  }

  updateMetric(project: any, newValue: number): void {
    project.metric_value.value = Math.max(
      this.minCap,
      Math.min(this.maxCap, newValue)
    );
    project.metric_value.status = this.getMetricStatusColor(project);
    this.calculateAverage();
  }

  updateMetricGoal(project: any, metric: any, newValue: number): void {
    // Clamp between min/max
    const minCap = metric.minCap ?? 0;
    const maxCap = metric.maxCap ?? 100;

    metric.value = Math.max(minCap, Math.min(maxCap, newValue));
    metric.status = this.getMetricStatusColorGoalMetrics(metric);
    metric.barLength = this.calculateBarLengthGoalMetric(metric.value, metric);

    // Recalculate only this metric’s average
    this.updateMetricSummary(metric.metric_id);
  }

  private summaryNumbering(): void {
    this.metric_summaries.forEach((summary: any) => {
      summary?.display_name;
    });
  }

  private updateMetricSummary(metricId: number): void {
    let total = 0;
    let count = 0;

    this.metric_data.portfolios.forEach((portfolio: any) => {
      portfolio.sub_portfolios.forEach((sub: any) => {
        sub.programs.forEach((program: any) => {
          program.projects.forEach((project: any) => {
            if (project.checked) {
              const metric = project.metrics.find(
                (m: any) => m.metric_id === metricId
              );
              if (metric) {
                total += metric.value;
                count++;
              }
            }
          });
        });
      });
    });

    const summary = this.metric_summaries.find(
      (m: any) => m.metric_id === metricId
    );
    if (summary) {
      summary.averageValue = count > 0 ? total / count : 0;
    }
  }

  calculateMetricSummaries() {
    this.metric_summaries.forEach((summary: any) => {
      this.updateMetricSummary(summary.metric_id);
    });
  }

  private calculateAverage(): void {
    let total = 0;
    let count = 0;

    this.metric_data.portfolios.forEach((portfolio: any) => {
      portfolio.sub_portfolios.forEach((sub: any) => {
        sub.programs.forEach((program: any) => {
          program.projects.forEach((project: any) => {
            if (project.checked) {
              total += project.metric_value.value;
              count++;
            }
          });
        });
      });
    });

    this.averageValue = count > 0 ? total / count : 0;
  }

  onProjectCheckboxChange(project: any, event: Event) {
    const checkbox = event.target as HTMLInputElement;
    project.checked = checkbox.checked;
    if (this.data.page === "okr convergence") {
      this.calculateMetricSummaries();
    } else {
      this.calculateAverage();
    }
  }

  onProjectCheckboxChangeGoal(project: any, event: Event) {
    project.checked = (event.target as HTMLInputElement).checked;
    this.calculateMetricSummaries(); // recalc all metric averages
  }

  trackByProject(index: number, project: any): number {
    return project.id;
  }

  trackByMetric(index: number, metric: any): number {
    return metric.metric_id;
  }

  reset(): void {
    this.metric_data = JSON.parse(JSON.stringify(this.initial_metric_data));
    if (this.data.page === "okr convergence") {
      this.setMinMaxGoalMetrics();
      this.initializeMetricsBarLength();
      this.buildMetricSummaries();
    } else {
      this.calculateAverage();
    }
  }

  getBreadCrumbData(queryParams: any, metricData: any): void {
    const breadcrumbs: string[] = [];
    if (
      !queryParams ||
      Object.keys(queryParams).length === 0 ||
      Object.values(queryParams).every(
        (v) => v === "" || v === null || v === undefined
      )
    ) {
      this.breadCrumbData = [this.displayLabels?.all_of_it];
      return;
    }

    const { project_id, program_id, subportfolio_id, portfolio_id } =
      queryParams;

    for (const portfolio of metricData?.portfolios) {
      for (const sub of portfolio?.sub_portfolios) {
        for (const program of sub?.programs) {
          for (const project of program?.projects) {
            if (project_id && project?.id === project_id) {
              this.breadCrumbData = [
                portfolio?.name,
                sub?.name,
                program?.name,
                project?.name,
              ];
              return;
            }
          }

          if (program_id && program.id === program_id) {
            this.breadCrumbData = [portfolio?.name, sub?.name, program?.name];
            return;
          }
        }

        if (subportfolio_id && sub?.id === subportfolio_id) {
          this.breadCrumbData = [portfolio?.name, sub?.name];
          return;
        }
      }

      if (portfolio_id && portfolio.id === portfolio_id) {
        this.breadCrumbData = [portfolio?.name];
        return;
      }
    }
  }

  getselectedFilterLevel(): string {
    if (this.query_params?.project_id) {
      return "project";
    } else if (this.query_params?.program_id) {
      return "program";
    } else if (this.query_params?.subportfolio_id) {
      return "sub-portfolio";
    } else if (this.query_params?.portfolio_id) {
      return "portfolio";
    } else {
      return "metric";
    }
  }

  getHTBStatusColor(
    currentValue: number,
    onTrack: number | null,
    offTrack: number | null,
    atRisk: number | null
  ): string {
    let color = "gray";

    if (onTrack !== null && currentValue >= onTrack) {
      color = "green";
    } else if (
      offTrack !== null &&
      onTrack !== null &&
      currentValue >= offTrack &&
      currentValue <= onTrack
    ) {
      color = "amber";
    } else if (
      (atRisk !== null && currentValue <= atRisk) ||
      (offTrack !== null && currentValue < offTrack)
    ) {
      color = "red";
    }

    return color;
  }

  getLTBStatusColor(
    currentValue: number,
    onTrack: number | null,
    offTrack: number | null,
    atRisk: number | null
  ): string {
    let color = "gray";

    if (onTrack !== null && currentValue <= onTrack) {
      color = "green";
    } else if (
      offTrack !== null &&
      onTrack !== null &&
      currentValue <= offTrack &&
      currentValue >= onTrack
    ) {
      color = "amber";
    } else if (
      (atRisk !== null && currentValue >= atRisk) ||
      (offTrack !== null && currentValue > offTrack)
    ) {
      color = "red";
    }

    return color;
  }

  convertToFloatOrNull(value: any): number | null {
    if (value === null || value === undefined) return null;
    const num = parseFloat(value);
    return isNaN(num) ? null : num;
  }

  getMetricStatusColor(project: any): string {
    const currentValue = project?.metric_value?.value ?? null;

    const onTrack = this.convertToFloatOrNull(this.metric_data?.on_track_value);
    const offTrack = this.convertToFloatOrNull(
      this.metric_data?.off_track_value
    );
    const atRisk = this.convertToFloatOrNull(this.metric_data?.at_risk_value);
    const mosType = this.metric_data?.mos_type_name;

    let color = "gray"; // default

    if ((onTrack !== null || offTrack !== null || atRisk !== null) && mosType) {
      if (mosType === "HTB") {
        if (currentValue !== null) {
          color = this.getHTBStatusColor(
            currentValue,
            onTrack,
            offTrack,
            atRisk
          );
        } else {
          color = this.getHTBStatusColor(
            this.averageValue,
            onTrack,
            offTrack,
            atRisk
          );
        }
      } else if (mosType === "LTB") {
        if (currentValue !== null) {
          color = this.getLTBStatusColor(
            currentValue,
            onTrack,
            offTrack,
            atRisk
          );
        } else {
          color = this.getLTBStatusColor(
            this.averageValue,
            onTrack,
            offTrack,
            atRisk
          );
        }
      }
    }

    return color;
  }

  getMetricStatusColorGoalMetrics(metric: any): string {
    if (!metric) return "gray";

    const currentValue = metric?.value ?? null;

    const onTrack = this.convertToFloatOrNull(metric?.on_track_value);
    const offTrack = this.convertToFloatOrNull(metric?.off_track_value);
    const atRisk = this.convertToFloatOrNull(metric?.at_risk_value);
    const mosType = metric?.mos_type_name;

    let color = "gray"; // default fallback

    if ((onTrack !== null || offTrack !== null || atRisk !== null) && mosType) {
      if (mosType === "HTB") {
        color = this.getHTBStatusColor(currentValue, onTrack, offTrack, atRisk);
      } else if (mosType === "LTB") {
        color = this.getLTBStatusColor(currentValue, onTrack, offTrack, atRisk);
      }
    }

    return color;
  }

  getHeaderStatus(status: string): string {
    return `status-${status}`;
  }

  calculateBarLength(value: any) {
    if (value !== null || value !== undefined) {
      const difference = this.maxCap - this.minCap;
      value = value - this.minCap;
      const barLength = (value / difference) * 100;
      if (barLength >= 0) {
        return barLength;
      }
    }
    return 0;
  }

  calculateBarLengthGoalMetric(value: number, metric: any): number {
    if (value == null || metric.minCap == null || metric.maxCap == null) {
      return 0;
    }

    const range = metric.maxCap - metric.minCap;
    if (range <= 0) return 0;

    const barLength = ((value - metric.minCap) / range) * 100;
    if (barLength >= 0) {
      return barLength;
    } else {
      return 0;
    }
  }

  getFormattedValue(val: number) {
    if (val) {
      val = parseFloat(val.toString());
      const value = getFormattedValWithSign(val);
      return value;
    }
    return val;
  }

  dialogClose() {
    this.dialogRef.close();
  }
}
